import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Alert,
  TextField,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getUserDetails } from 'modules/platform-components/utils/shared-data.service';
import { usePermission } from 'modules/platform-components/utils/PermissionGate';
import { filterBySubjectScope } from 'modules/platform-components/utils/subject-scope';
import {
  apiPaths,
  getInstutueApp,
  patchInstituteAppData,
  deleteInstituteAppData,
} from 'modules/platform-components/api-integration-util';

const getHeaders = () => {
  const user = getUserDetails();
  return { tenantId: user.tenantId, userEmail: user.email };
};

// ─── Status config ──────────────────────────────────────────
const statusConfig = {
  pending:  { label: 'Pending',  color: '#f59e0b' },
  approved: { label: 'Approved', color: '#22c55e' },
  rejected: { label: 'Rejected', color: '#dc3545' },
};

// ─── Stats Dashboard ────────────────────────────────────────
function StatsDashboard({ stats }) {
  if (!stats) return null;

  const cards = [
    { label: 'Pending',  value: stats.pending  ?? 0, color: '#f59e0b', bg: '#fffbeb' },
    { label: 'Approved', value: stats.approved ?? 0, color: '#22c55e', bg: '#f0fdf4' },
    { label: 'Rejected', value: stats.rejected ?? 0, color: '#dc3545', bg: '#fef2f2' },
    { label: 'Total',    value: stats.total    ?? 0, color: '#3366FF', bg: '#eef2ff' },
  ];

  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
      {cards.map((card) => (
        <Paper
          key={card.label}
          elevation={1}
          sx={{
            p: 2,
            minWidth: 140,
            textAlign: 'center',
            backgroundColor: card.bg,
            borderLeft: `4px solid ${card.color}`,
          }}
        >
          <Typography variant="h4" sx={{ color: card.color, fontWeight: 'bold' }}>
            {card.value}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
            {card.label}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
}

// ─── Status chip for variation rows ─────────────────────────
function StatusChip({ status }) {
  const cfg = statusConfig[status] || { label: status, color: '#999' };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        backgroundColor: cfg.color,
        color: '#fff',
        fontWeight: 'bold',
        fontSize: '0.7rem',
      }}
    />
  );
}

// ─── Mini status summary for table rows ─────────────────────
function StatusSummary({ pendingCount, approvedCount, rejectedCount }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {pendingCount > 0 && (
        <Chip label={`${pendingCount} Pending`} size="small"
          sx={{ backgroundColor: '#fffbeb', color: '#d97706', fontSize: '0.7rem', fontWeight: 600 }} />
      )}
      {approvedCount > 0 && (
        <Chip label={`${approvedCount} Approved`} size="small"
          sx={{ backgroundColor: '#f0fdf4', color: '#22c55e', fontSize: '0.7rem', fontWeight: 600 }} />
      )}
      {rejectedCount > 0 && (
        <Chip label={`${rejectedCount} Rejected`} size="small"
          sx={{ backgroundColor: '#fef2f2', color: '#dc3545', fontSize: '0.7rem', fontWeight: 600 }} />
      )}
    </Box>
  );
}

// ─── Detail Dialog ──────────────────────────────────────────
function DetailDialog({ open, onClose, questionGroup, onApprove, onReject, onDelete, onApproveAll, actionLoading }) {
  const canApprove = usePermission('QA Actions', 'Approve Rephrase');
  const canReject = usePermission('QA Actions', 'Reject Rephrase');
  const canDelete = usePermission('QA Actions', 'Delete Rephrase');
  if (!open || !questionGroup) return null;

  const { questionId, questionText, variations = [] } = questionGroup;
  const hasPending = variations.some(v => v.status === 'pending');

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Question #{questionId} — Rephrased Variations
        </Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Original Question */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ color: '#666', mb: 1, fontWeight: 'bold' }}>
            ORIGINAL QUESTION
          </Typography>
          <Paper
            elevation={0}
            sx={{ p: 2, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}
          >
            <div dangerouslySetInnerHTML={{ __html: questionText || 'Question text not available' }} />
          </Paper>
        </Box>

        {/* Variations */}
        {variations.map((variation, index) => (
          <Box key={variation.id} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 'bold' }}>
                VARIATION {index + 1}
              </Typography>
              <StatusChip status={variation.status} />
            </Box>
            <Paper
              elevation={0}
              sx={{ p: 2, backgroundColor: '#fafafa', border: '1px solid #e0e0e0', mb: 1 }}
            >
              <div dangerouslySetInnerHTML={{ __html: variation.variationText }} />
            </Paper>

            {/* Action buttons for pending variations */}
            {variation.status === 'pending' && (canApprove || canReject || canDelete) && (
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                {canApprove && (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    disabled={actionLoading}
                    onClick={() => onApprove(variation.id)}
                  >
                    Approve
                  </Button>
                )}
                {canReject && (
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    startIcon={<CancelIcon />}
                    disabled={actionLoading}
                    onClick={() => onReject(variation.id)}
                  >
                    Reject
                  </Button>
                )}
                {canDelete && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    disabled={actionLoading}
                    onClick={() => onDelete(variation.id)}
                  >
                    Delete
                  </Button>
                )}
              </Box>
            )}

            {/* Info for approved/rejected */}
            {variation.status === 'approved' && variation.approvedBy && (
              <Typography variant="caption" sx={{ color: '#666' }}>
                Approved by {variation.approvedBy} on {new Date(variation.approvedDate).toLocaleDateString()}
              </Typography>
            )}
            {variation.status === 'rejected' && variation.approvedBy && (
              <Typography variant="caption" sx={{ color: '#666' }}>
                Rejected by {variation.approvedBy} on {new Date(variation.approvedDate).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        ))}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Box>
          {hasPending && canApprove && (
            <Button
              variant="contained"
              color="success"
              startIcon={<DoneAllIcon />}
              disabled={actionLoading}
              onClick={onApproveAll}
            >
              Approve All Pending
            </Button>
          )}
        </Box>
        <Button onClick={onClose} variant="outlined">Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Component ─────────────────────────────────────────
export default function RephrasedQuestions() {
  const [questions, setQuestions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reviewedByFilter, setReviewedByFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogVariations, setDialogVariations] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const PAGE_SIZE = 20;

  // ─── Fetch grouped variations ────────────────────────────
  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getHeaders();
      const params = {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      };
      if (statusFilter) {
        params.status = statusFilter;
      }
      if (dateFrom) params.fromDate = dateFrom;
      if (dateTo) params.toDate = dateTo;
      if (reviewedByFilter) params.reviewedBy = reviewedByFilter;
      const data = await getInstutueApp(
        apiPaths.rephrasedQuestions,
        params,
        headers
      );
      // Client-side subject scope filter (backend scoping pending).
      // If API doesn't return subjectId on grouped rows, items pass through (null-safe).
      setQuestions(
        filterBySubjectScope(data.questions || [], (q) => q.subjectId ?? q.subject_id)
      );
      setTotalQuestions(data.totalQuestions || 0);
    } catch (e) {
      toast.error('Failed to load rephrased questions');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, dateFrom, dateTo, reviewedByFilter]);

  // ─── Fetch stats ─────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    try {
      const headers = getHeaders();
      const data = await getInstutueApp(apiPaths.rephrasedQuestionsStats, {}, headers);
      setStats(data);
    } catch (e) {
      console.error('Failed to load stats', e);
    }
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // ─── Open detail dialog ──────────────────────────────────
  const handleViewQuestion = async (questionGroup) => {
    setDialogOpen(true);
    setDialogLoading(true);
    try {
      // Fetch all variations for this question (all statuses)
      const headers = getHeaders();
      const variations = await getInstutueApp(
        `${apiPaths.rephrasedQuestionVariations}/${questionGroup.questionId}/variations`,
        {},
        headers
      );
      setDialogVariations({
        ...questionGroup,
        variations: variations || questionGroup.variations,
      });
    } catch (e) {
      // Fall back to the grouped data we already have
      setDialogVariations(questionGroup);
      toast.warn('Could not fetch full variations, showing filtered view');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogVariations(null);
  };

  // ─── Approve variation ───────────────────────────────────
  const handleApprove = async (variationId) => {
    setActionLoading(true);
    try {
      const headers = getHeaders();
      await patchInstituteAppData(
        `${apiPaths.rephrasedQuestionApprove}/${variationId}/approve`,
        { headers }
      );
      toast.success('Variation approved');
      // Refresh dialog data
      if (dialogVariations) {
        setDialogVariations(prev => ({
          ...prev,
          variations: prev.variations.map(v =>
            v.id === variationId ? { ...v, status: 'approved', approvedBy: getUserDetails().email, approvedDate: new Date() } : v
          ),
        }));
      }
      fetchQuestions();
      fetchStats();
    } catch (e) {
      toast.error('Failed to approve variation');
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Reject variation ────────────────────────────────────
  const handleReject = async (variationId) => {
    setActionLoading(true);
    try {
      const headers = getHeaders();
      await patchInstituteAppData(
        `${apiPaths.rephrasedQuestionReject}/${variationId}/reject`,
        { headers }
      );
      toast.success('Variation rejected');
      if (dialogVariations) {
        setDialogVariations(prev => ({
          ...prev,
          variations: prev.variations.map(v =>
            v.id === variationId ? { ...v, status: 'rejected', approvedBy: getUserDetails().email, approvedDate: new Date() } : v
          ),
        }));
      }
      fetchQuestions();
      fetchStats();
    } catch (e) {
      toast.error('Failed to reject variation');
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Delete variation ────────────────────────────────────
  const handleDelete = async (variationId) => {
    if (!window.confirm('Are you sure you want to delete this variation?')) return;
    setActionLoading(true);
    try {
      const headers = getHeaders();
      await deleteInstituteAppData(
        `${apiPaths.rephrasedQuestionDelete}/${variationId}`,
        { headers }
      );
      toast.success('Variation deleted');
      if (dialogVariations) {
        setDialogVariations(prev => ({
          ...prev,
          variations: prev.variations.filter(v => v.id !== variationId),
        }));
      }
      fetchQuestions();
      fetchStats();
    } catch (e) {
      toast.error('Failed to delete variation');
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Approve all pending for a question ──────────────────
  const handleApproveAll = async () => {
    if (!dialogVariations) return;
    const pendingIds = dialogVariations.variations
      .filter(v => v.status === 'pending')
      .map(v => v.id);

    if (pendingIds.length === 0) return;

    setActionLoading(true);
    try {
      const headers = getHeaders();
      for (const id of pendingIds) {
        await patchInstituteAppData(
          `${apiPaths.rephrasedQuestionApprove}/${id}/approve`,
          { headers }
        );
      }
      toast.success(`Approved ${pendingIds.length} variation(s)`);
      setDialogVariations(prev => ({
        ...prev,
        variations: prev.variations.map(v =>
          v.status === 'pending'
            ? { ...v, status: 'approved', approvedBy: getUserDetails().email, approvedDate: new Date() }
            : v
        ),
      }));
      fetchQuestions();
      fetchStats();
    } catch (e) {
      toast.error('Failed to approve all variations');
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Handle filter change ────────────────────────────────
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const handleRefresh = () => {
    fetchQuestions();
    fetchStats();
  };

  const totalPages = Math.ceil(totalQuestions / PAGE_SIZE);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Rephrased Questions
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stats */}
      <StatsDashboard stats={stats} />

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status Filter</InputLabel>
          <Select
            value={statusFilter}
            onChange={handleStatusFilterChange}
            label="Status Filter"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small" type="date" label="From Date" value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }}
        />
        <TextField
          size="small" type="date" label="To Date" value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }}
        />
        <TextField
          size="small" label="Reviewed By" value={reviewedByFilter}
          onChange={(e) => { setReviewedByFilter(e.target.value); setPage(1); }}
          placeholder="Email or name" sx={{ minWidth: 160 }}
        />
        {(dateFrom || dateTo || reviewedByFilter) && (
          <Chip
            label="Clear date/user filters"
            onDelete={() => { setDateFrom(''); setDateTo(''); setReviewedByFilter(''); setPage(1); }}
            size="small" color="primary" variant="outlined"
          />
        )}
        <Typography variant="body2" sx={{ color: '#666' }}>
          {totalQuestions} question{totalQuestions !== 1 ? 's' : ''} found
        </Typography>
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty state */}
      {!loading && questions.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No rephrased questions found{statusFilter ? ` with status "${statusFilter}"` : ''}.
        </Alert>
      )}

      {/* Table */}
      {!loading && questions.length > 0 && (
        <>
          <TableContainer component={Paper} elevation={1}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: 100 }}>Q. ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Original Question</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 220 }}>Variations</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 130 }}>Created By</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 110 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 80, textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {questions.map((q) => (
                  <TableRow key={q.questionId} hover sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Q-{q.questionId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {q.questionPlainText || 'Question text not available'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusSummary
                        pendingCount={q.pendingCount}
                        approvedCount={q.approvedCount}
                        rejectedCount={q.rejectedCount}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {q.createdBy}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {q.createdDate ? new Date(q.createdDate).toLocaleDateString() : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Tooltip title="View Variations">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewQuestion(q)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, val) => setPage(val)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      {/* Detail Dialog */}
      <DetailDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        questionGroup={dialogLoading ? null : dialogVariations}
        onApprove={handleApprove}
        onReject={handleReject}
        onDelete={handleDelete}
        onApproveAll={handleApproveAll}
        actionLoading={actionLoading}
      />

      {/* Loading spinner inside dialog when fetching variations */}
      {dialogOpen && dialogLoading && (
        <Dialog open={true} maxWidth="sm">
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
}
